import { Outlet } from "@tanstack/react-router";
import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { setLegalRequiredHandler } from "../lib/api";
import { LegalAcceptPoster } from "../components/legal/accept-poster";
import { meQuery, qk } from "../lib/queries";
import { CheckoutModal } from "../components/shell/checkout-modal";
import { DevStrip } from "../components/dev/dev-strip";

/**
 * The root, and it is now almost nothing.
 *
 * It used to be the shell: a rail of rooms down the left, an identity chip at
 * the bottom of it, a tab bar on a phone and a 600-pixel column in the middle.
 * All of that has moved INSIDE the one screen that is the product — the
 * messenger shell at `/` — because the navigation and the content stopped being
 * separable the moment the conversation list became the navigation.
 *
 * What is left is the router's mount point. Each route now brings its own
 * frame: the shell brings a full viewport, and the three remaining pages bring
 * a reading column (`components/ui/page.tsx`). That is not a loss of a shared
 * layer — it is the honest shape of an app with one screen and three
 * outbuildings, and it means a change to the shell can no longer reach a
 * stranger's door by accident.
 */
export function RootLayout() {
  /**
   * THE LEGAL BLOCKING SCREEN, IN FRONT OF EVERYTHING (ruling R-0919-2).
   *
   * Here rather than on `/`, because "no other navigation works" has to be
   * true of every address: this is the router's mount point, so the poster
   * takes the OUTLET'S place and there is nothing behind it to reach. The
   * checkout modal and the dev strip go with it — a checkout somebody cannot
   * finish is worse than one they cannot start.
   *
   * IT IS A READ, AND THE SERVER DECIDED IT. `/me` says what is pending; this
   * renders it. A stale tab that has not re-read is caught by the other half
   * of the mechanism — any write it tries answers 403 and `lib/api/core.ts`
   * invalidates this query, so the poster appears on the tab that tried.
   *
   * `undefined` is a cache miss mid-flight and means nothing at all, which is
   * why this asks for `pending.length` rather than for truthiness: a poster
   * flashed over the app while `/me` is in the air would be a worse bug than
   * the one it is preventing.
   */
  const queryClient = useQueryClient();
  /**
   * THE OTHER HALF, WIRED HERE because this is where the poster is drawn and
   * because it is the one component mounted for the whole life of the app.
   * `lib/api/core.ts` sees a 403 carrying the gate's code on any write and
   * calls this; all it does is re-read `/me`, which is the only thing that
   * decides whether the poster appears. The client still works nothing out.
   */
  useEffect(() => {
    setLegalRequiredHandler(() => {
      void queryClient.invalidateQueries({ queryKey: qk.me() });
    });
    return () => setLegalRequiredHandler(null);
  }, [queryClient]);

  const { data: me } = useQuery(meQuery());
  if (me && me.legal.pending.length > 0) return <LegalAcceptPoster legal={me.legal} />;

  return (
    <>
      <Outlet />
      {/*
        Checkout (ruling R-0825-2). It is here, above the router's outlet, for
        the same reason the strip below it is: the surfaces that start a
        purchase are spread across four addresses, and a modal owned by any one
        of them would close the moment that one re-rendered. It draws nothing at
        all until something starts a checkout.
      */}
      <CheckoutModal />
      {/*
        The one thing that outlives every route: the flow-testing strip. It
        belongs here rather than inside the shell because the flows it exists
        for cross addresses — a knocker leaves for somebody else's door, a
        trial comes back at `/you/plan` — and a strip that vanished at the door
        would be missing exactly where "reset and try the other branch" is the
        next thing a tester wants. It renders nothing at all unless the API is
        on the local identity stand-in AND the account is a disposable one.
      */}
      <DevStrip />
    </>
  );
}
